import { Link, useSearchParams } from 'react-router';
import { RefreshCcw } from 'lucide-react';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Badge, Button, EmptyState, ErrorState, Input } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { useAdminUsers } from '@/hooks/useAdmin';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import type { AdminRecordStatus, AdminUserItem } from '@/services/adminClient';
import { useUserPreferences } from './UserPreferencesProvider';
import { Pagination, QueryError, TenantPicker } from './SettingsAdminControls';
import { LoadingRow, selectClassName } from './settingsPanels';
import {
  SettingsField,
  SettingsTable,
  SettingsTableRow,
  SettingsTd,
  settingsFilterRowClass,
} from './settingsChrome';

const statuses: AdminRecordStatus[] = ['ACTIVE', 'INACTIVE', 'ARCHIVED', 'DELETED'];

/** Both settings routes read the same directory, scope, filters, and account state. */
export function SettingsUserDirectory({ onManage }: { onManage?: (user: AdminUserItem) => void }) {
  const scope = useSessionScopeFence(PERMISSIONS.USERS_MANAGE);
  if (!scope.enabled) {
    return (
      <ErrorState
        title="User administration unavailable"
        description="Sign in with permission to manage users."
      />
    );
  }
  return <Directory key={JSON.stringify(sessionScopeKey(scope.identity))} onManage={onManage} />;
}

function Directory({ onManage }: { onManage?: (user: AdminUserItem) => void }) {
  const auth = useAuth();
  const { formatDateTime } = useUserPreferences();
  const platform = hasPermission(auth.permissions, PERMISSIONS.PLATFORM_ADMIN);
  const currentTenantId = auth.tenant?.id ?? auth.user?.tenantId ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const requestedStatus = searchParams.get('status');
  const status = statuses.includes(requestedStatus as AdminRecordStatus)
    ? (requestedStatus as AdminRecordStatus)
    : '';
  const requestedPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const tenantId = platform
    ? searchParams.get('directoryScope') === 'all'
      ? ''
      : searchParams.get('tenantId') || currentTenantId
    : currentTenantId;
  const users = useAdminUsers({
    page,
    pageSize: 25,
    q: q.trim() || undefined,
    tenantId: tenantId || undefined,
    status: status || undefined,
  });
  const items = users.data?.items ?? [];
  const meta = users.data?.meta;
  const changeFilter = (key: 'q' | 'tenantId' | 'status' | 'page', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    if (key === 'tenantId') {
      if (value) next.delete('directoryScope');
      else next.set('directoryScope', 'all');
    }
    setSearchParams(next, { replace: true });
  };
  const manageHref = (user: AdminUserItem) => {
    const next = new URLSearchParams(searchParams);
    next.set('userId', user.id);
    return `/settings/users?${next.toString()}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">
          {tenantId === currentTenantId
            ? `Current workspace: ${auth.tenant?.name ?? 'your workspace'}.`
            : tenantId
              ? 'Selected workspace.'
              : 'All workspaces.'}{' '}
          Users and Access control show the same accounts and sign-in status.
        </p>
        <Button type="button" variant="outline" size="xs" onClick={() => void users.refetch()}>
          <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Refresh users
        </Button>
      </div>
      <div
        className={`${settingsFilterRowClass} ${platform ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
      >
        <SettingsField label="Search users">
          <Input
            value={q}
            maxLength={120}
            onChange={(event) => changeFilter('q', event.target.value)}
            placeholder="Name or email"
          />
        </SettingsField>
        {platform ? (
          <TenantPicker
            value={tenantId}
            onChange={(id) => changeFilter('tenantId', id)}
            label="Tenant filter"
            allOption="All tenants"
            fallback={{ id: currentTenantId, name: auth.tenant?.name ?? 'Current workspace' }}
          />
        ) : null}
        <SettingsField label="Status filter">
          <select
            className={selectClassName}
            value={status}
            onChange={(event) => changeFilter('status', event.target.value)}
          >
            <option value="">All retained users</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ARCHIVED">Archived</option>
            <option value="DELETED">Removed</option>
          </select>
        </SettingsField>
      </div>
      {users.isLoading ? (
        <LoadingRow label="Loading users…" />
      ) : users.isError ? (
        <QueryError
          title="Users unavailable"
          error={users.error}
          retry={() => void users.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No matching users"
          description="Adjust the filters or create an account with explicitly selected access."
        />
      ) : (
        <SettingsTable
          columns={[
            'User',
            ...(platform ? ['Tenant'] : []),
            'Roles',
            'Status',
            'Sign-in access',
            'Last login',
            'Actions',
          ]}
          minWidth={920}
          label="User directory"
        >
          {items.map((user) => (
            <SettingsTableRow key={user.id}>
              <SettingsTd>
                <p className="text-foreground font-medium">{user.name}</p>
                <p className="text-muted-foreground">{user.email}</p>
              </SettingsTd>
              {platform ? <SettingsTd>{user.tenantName}</SettingsTd> : null}
              <SettingsTd>{user.roles.map((role) => role.name).join(', ') || 'None'}</SettingsTd>
              <SettingsTd>
                <Badge variant={user.status === 'ACTIVE' ? 'success' : 'outline'}>
                  {user.deletedAt ? 'Removed' : user.status}
                </Badge>
              </SettingsTd>
              <SettingsTd>
                <UserAccessState user={user} />
              </SettingsTd>
              <SettingsTd>
                {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never'}
              </SettingsTd>
              <SettingsTd>
                {onManage ? (
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    aria-label={`Manage ${user.name}`}
                    onClick={() => onManage(user)}
                  >
                    Manage
                  </Button>
                ) : (
                  <Button size="xs" variant="outline" asChild>
                    <Link aria-label={`Manage ${user.name}`} to={manageHref(user)}>
                      Manage
                    </Link>
                  </Button>
                )}
              </SettingsTd>
            </SettingsTableRow>
          ))}
        </SettingsTable>
      )}
      {meta ? (
        <p className="text-2xs text-muted-foreground">
          {meta.total} matching account{meta.total === 1 ? '' : 's'}.
        </p>
      ) : null}
      <Pagination
        page={page}
        totalPages={meta?.totalPages ?? 0}
        onChange={(value) => changeFilter('page', String(value))}
        label="Users"
      />
    </div>
  );
}

export function UserAccessState({ user }: { user: AdminUserItem }) {
  if (user.deletedAt || !user.authenticationEnabled)
    return <Badge variant="outline">No sign-in access</Badge>;
  if (user.status !== 'ACTIVE' || user.tenantStatus !== 'ACTIVE')
    return <Badge variant="warning">Suspended</Badge>;
  if (user.mustChangePassword) return <Badge variant="warning">Password change required</Badge>;
  return <Badge variant="success">Enabled</Badge>;
}
